import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AuthPage from './components/auth/AuthPage';
import GamePage from './components/GamePage';
import AdminDashboard from './components/admin/AdminDashboard';
import UserManagement from './components/admin/UserManagement';
import PlayerDashboard from './components/player/PlayerDashboard';
import JoinGame from './components/player/JoinGame';
import SpectatorEntry from './components/spectator/SpectatorEntry';
import SpectatorGame from './components/spectator/SpectatorGame';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import './App.css';

// Protected route component (for non-admin users)
const ProtectedRoute = ({ children, adminRedirect = false }) => {
  const { currentUser, isAdmin } = useAuth();

  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  // If adminRedirect is true and user is admin, redirect to admin dashboard
  if (adminRedirect && isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

// Admin-only route component
const AdminRoute = ({ children }) => {
  const { currentUser, isAdmin } = useAuth();

  console.log('🔐 AdminRoute check - User:', currentUser?.email, 'isAdmin:', isAdmin);

  if (!currentUser) {
    console.log('❌ No user, redirecting to /auth');
    return <Navigate to="/auth" replace />;
  }

  if (!isAdmin) {
    console.log('❌ Not admin, redirecting to /game');
    return <Navigate to="/game" replace />;
  }

  console.log('✅ Admin access granted!');
  return children;
};

// Public route component (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { currentUser, isAdmin } = useAuth();

  console.log('🔓 PublicRoute check - User:', currentUser?.email, 'isAdmin:', isAdmin);

  if (currentUser) {
    const redirectTo = isAdmin ? "/admin" : "/game";
    console.log('✅ User logged in, redirecting to:', redirectTo);
    // Redirect admins to admin dashboard, regular users to game
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Router>
      <Routes>
        <Route
          path="/auth"
          element={
            <PublicRoute>
              <AuthPage />
            </PublicRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <UserManagement />
            </AdminRoute>
          }
        />
        <Route
          path="/game"
          element={
            <ProtectedRoute adminRedirect={true}>
              <PlayerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/game/:gameId"
          element={
            <ProtectedRoute>
              <GamePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/join/:inviteCode"
          element={
            <ProtectedRoute>
              <JoinGame />
            </ProtectedRoute>
          }
        />
        <Route path="/spectate" element={<SpectatorEntry />} />
        <Route path="/spectate/:gameId" element={<SpectatorGame />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/" element={<Navigate to="/auth" replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
